import * as stylex from '@stylexjs/stylex'
import type { ReactNode } from 'react'

import SettleUpLogo from './SettleUpLogo'
import Avatar from './Avatar'
import Icon, { type IconName } from './Icon'
import type { User } from '../lib/ledger'

export type AppSection = 'dashboard' | 'groups' | 'people' | 'loans' | 'activity'


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  s1: {
    height: "19px",
    width: "19px",
  },
  s2: {
    minHeight: "100vh",
    backgroundColor: "#f8faf7",
    color: "#222723",
  },
  s3: {
    position: "fixed",
    insetBlock: "0px",
    left: "0px",
    zIndex: "20",
    display: "none",
    width: "256px",
    flexDirection: "column",
    borderRightWidth: 1,
    borderColor: "#e3e8e1",
    backgroundColor: "#fffefa",
    paddingInline: "20px",
    paddingBlock: "24px",
    "@media (min-width: 1024px)": {
      display: "flex",
    },
  },
  s4: {
    paddingInline: "4px",
  },
  s5: {
    marginTop: "40px",
  },
  s6: {
    marginTop: "32px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  s7: {
    display: "flex",
    height: "44px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 4px 12px rgba(43,112,62,.18)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347845",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#347845",
    },
  },
  s8: {
    height: "18px",
    width: "18px",
  },
  s9: {
    display: "flex",
    height: "40px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dce5da",
    backgroundColor: "#fff",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#42624b",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f5f9f4",
    },
  },
  s10: {
    marginTop: "auto",
    borderTopWidth: 1,
    borderColor: "#edf0eb",
    paddingTop: "20px",
  },
  s17: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: "12px",
    borderRadius: "0.75rem",
    padding: "8px",
    textAlign: "left",
    transition: "background-color, box-shadow, transform",
    ":hover": {
      backgroundColor: "#f3f8f1",
      boxShadow: "0 1px 2px 0 rgb(48 70 51 / 0.08)",
    },
  },
  s18: {
    minWidth: "0px",
  },
  s19: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 600,
    color: "#29322b",
  },
  s20: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: "#778078",
  },
  s21: {
    marginTop: "12px",
    height: "36px",
    width: "100%",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dce6da",
    fontSize: "12px",
    fontWeight: 600,
    color: "#59705e",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f3f7f2",
    },
  },
  s22: {
    "@media (min-width: 1024px)": {
      paddingLeft: "256px",
    },
  },
  s23: {
    position: "sticky",
    top: "0px",
    zIndex: "10",
    display: "flex",
    height: "64px",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderColor: "#e7ece5",
    backgroundColor: "color-mix(in srgb, #fffefa 95%, transparent)",
    paddingInline: "16px",
    backdropFilter: "blur(8px)",
    "@media (min-width: 1024px)": {
      display: "none",
    },
  },
  s24: {
    transformOrigin: "left",
    transform: "scale(.9)",
  },
  s25: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  s26: {
    display: "flex",
    height: "40px",
    width: "40px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dce8da",
    backgroundColor: "#fff",
    color: "#477151",
  },
  s27: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#438b57",
    paddingInline: "14px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
  },
  s28: {
    marginInline: "auto",
    minHeight: "100vh",
    maxWidth: "1440px",
    paddingInline: "16px",
    paddingBottom: "96px",
    paddingTop: "24px",
    "@media (min-width: 640px)": {
      paddingInline: "24px",
      paddingTop: "32px",
    },
    "@media (min-width: 1024px)": {
      paddingInline: "36px",
      paddingBottom: "40px",
      paddingTop: "36px",
    },
  },
  s29: {
    position: "fixed",
    insetInline: "0px",
    bottom: "0px",
    zIndex: "20",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    borderTopWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "color-mix(in srgb, #fffefa 95%, transparent)",
    paddingInline: "8px",
    paddingBottom: "max(.45rem,env(safe-area-inset-bottom))",
    paddingTop: "6px",
    backdropFilter: "blur(8px)",
    "@media (min-width: 1024px)": {
      display: "none",
    },
  },
  s30: {
    position: "fixed",
    bottom: "2.65rem",
    left: "50%",
    zIndex: "30",
    display: "flex",
    height: "56px",
    width: "56px",
    transform: "translateY(0px)",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    borderWidth: 4,
    borderColor: "#f8faf7",
    backgroundColor: "#3f8955",
    color: "#fff",
    boxShadow: "0 5px 16px rgba(43,112,62,.32)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347845",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#347845",
    },
    "@media (min-width: 1024px)": {
      display: "none",
    },
  },
  s31: {
    height: "24px",
    width: "24px",
  },
  s32: {
    marginTop: "12px",
    textAlign: "center",
    fontSize: "11px",
    color: "#8a958c",
  },
})

const navigationStyles = stylex.create({
  desktop: { display: 'flex', height: '44px', width: '100%', alignItems: 'center', gap: '12px', borderRadius: '0.75rem', paddingInline: '14px', textAlign: 'left', fontSize: '14px', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  desktopSelected: { backgroundColor: '#4b9160', fontWeight: 500, color: '#fff', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
  desktopIdle: { color: '#5d6760', ':hover': { backgroundColor: '#eef3ed', color: '#315f3e' } },
  mobile: { display: 'flex', minHeight: '52px', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '0.5rem', fontSize: '10px', fontWeight: 500 },
  mobileSelected: { color: '#397d4d' },
  mobileIdle: { color: '#788178' },
})
type AppShellProps = {
  active: AppSection
  children: ReactNode
  currentUser: User
  onAddExpense: () => void
  onCreateGroup: () => void
  onNavigate: (section: AppSection) => void
  onLogout: () => void
  onOpenProfile: () => void
}

const navigation: Array<{ section: AppSection; label: string; icon: IconName }> = [
  { section: 'dashboard', label: 'Overview', icon: 'dashboard' },
  { section: 'groups', label: 'Groups', icon: 'groups' },
  { section: 'people', label: 'People', icon: 'people' },
  { section: 'loans', label: 'Loans', icon: 'loans' },
  { section: 'activity', label: 'Activity', icon: 'activity' },
]

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev'
const APP_VERSION_LABEL = APP_VERSION === 'dev' ? 'dev' : `v${APP_VERSION}`

function Navigation({ active, onNavigate }: Pick<AppShellProps, 'active' | 'onNavigate'>) {
  return (
    <nav aria-label="Main navigation" {...stylex.props(styles.s0)}>
      {navigation.map((item) => {
        const selected = active === item.section
        return (
          <button aria-current={selected ? 'page' : undefined} {...stylex.props(navigationStyles.desktop, selected ? navigationStyles.desktopSelected : navigationStyles.desktopIdle)} key={item.section} onClick={() => onNavigate(item.section)} type="button">
            <Icon {...stylex.props(styles.s1)} name={item.icon} />
            {item.label}
          </button>
        )
      })}
    </nav>
  )
}

export default function AppShell({ active, children, currentUser, onAddExpense, onCreateGroup, onNavigate, onLogout, onOpenProfile }: AppShellProps) {
  return (
    <div {...stylex.props(styles.s2)}>
      <aside {...stylex.props(styles.s3)}>
        <SettleUpLogo {...stylex.props(styles.s4)} />
        <div {...stylex.props(styles.s5)}><Navigation active={active} onNavigate={onNavigate} /></div>

        <div {...stylex.props(styles.s6)}>
          <button {...stylex.props(styles.s7)} onClick={onAddExpense} type="button"><Icon {...stylex.props(styles.s8)} name="plus" />Add expense</button>
          <button {...stylex.props(styles.s9)} onClick={onCreateGroup} type="button"><Icon {...stylex.props(styles.s8)} name="groups" />New group</button>
        </div>

        <div {...stylex.props(styles.s10)}>
          <button aria-label="Open your profile" {...stylex.props(styles.s17)} onClick={onOpenProfile} type="button">
          <Avatar person={currentUser} size={38} />
          <div {...stylex.props(styles.s18)}><p {...stylex.props(styles.s19)}>{currentUser.name}</p><p {...stylex.props(styles.s20)}>{currentUser.email}</p></div>
          </button><button {...stylex.props(styles.s21)} onClick={onLogout} type="button">Log out</button>
          <p {...stylex.props(styles.s32)}>{APP_VERSION_LABEL}</p>
        </div>
      </aside>

      <div {...stylex.props(styles.s22)}>
        <header {...stylex.props(styles.s23)}>
          <SettleUpLogo {...stylex.props(styles.s24)} />
          <div {...stylex.props(styles.s25)}><button aria-label="Add expense" {...stylex.props(styles.s27)} onClick={onAddExpense} type="button"><Icon {...stylex.props(styles.s8)} name="plus" />Add</button></div>
        </header>
        <main {...stylex.props(styles.s28)}>{children}</main>
      </div>

      <nav aria-label="Main navigation" {...stylex.props(styles.s29)}>
        {navigation.map((item) => {
          const selected = active === item.section
          return <button aria-current={selected ? 'page' : undefined} {...stylex.props(navigationStyles.mobile, selected ? navigationStyles.mobileSelected : navigationStyles.mobileIdle)} key={item.section} onClick={() => onNavigate(item.section)} type="button"><Icon {...stylex.props(styles.s1)} name={item.icon} />{item.label}</button>
        })}
      </nav>
      <button aria-label="Add expense" {...stylex.props(styles.s30)} onClick={onAddExpense} type="button"><Icon {...stylex.props(styles.s31)} name="plus" /></button>
    </div>
  )
}
